import { forwardRef } from 'react';
import { Button, Icons, Stack, SxProps } from '@/theme/index';
import { useSwiperReactive } from '@/utils/hooks/useSwiperReactive/useSwiperReactive';

/**
 * BasicSwiperCarouselPagination properties.
 */
interface BasicSwiperCarouselPaginationProps {
  /** The total number of images in the carousel. */
  numberOfImages: number;
  customButtonsStyle?: SxProps;
  customContainerStyle?: SxProps;
}

/**
 * A basic pagination component for the Swiper carousel.
 *
 * @param {BasicSwiperCarouselPaginationProps} props - The properties.
 * @param {Ref<HTMLDivElement>} ref - Ref to the pagination component.
 * @returns {JSX.Element} The BasicSwiperCarouselPagination component.
 */

export const BasicSwiperCarouselPagination = forwardRef<
  HTMLDivElement,
  BasicSwiperCarouselPaginationProps
>(({ numberOfImages, customButtonsStyle, customContainerStyle }, ref) => {
  const { swiper, imagesLength } = useSwiperReactive();
  const isTheFirstImage = swiper?.activeIndex === 0;
  const isTheLastImage = swiper?.activeIndex + 1 === numberOfImages;

  return (
    <Stack
      ref={ref}
      id="basic-swiper-pagination"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={customContainerStyle}
    >
      <Button
        data-testid="basic-swiper-pagination-left-button"
        disabled={isTheFirstImage}
        onClick={() => swiper?.slidePrev()}
        color="success"
        sx={{
          ...customButtonsStyle,
          ...(isTheFirstImage &&
            customButtonsStyle && {
              borderColor: `#e9e7f2`,
            }),
        }}
      >
        <Icons.ChevronLeftRounded />
      </Button>
      {swiper?.activeIndex + 1} / {imagesLength || numberOfImages}
      <Button
        data-testid="basic-swiper-pagination-right-button"
        disabled={isTheLastImage}
        onClick={() => swiper?.slideNext()}
        color="success"
        sx={{
          ...customButtonsStyle,
          ...(isTheLastImage &&
            customButtonsStyle && {
              borderColor: `#e9e7f2`,
            }),
        }}
      >
        <Icons.ChevronRightRounded />
      </Button>
    </Stack>
  );
});
